/* import __COLOCATED_TEMPLATE__ from './class-group-results-card.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import dayjs from 'dayjs';
import {
	resultsSortFn as sharedResultsSortFn,
	sprintSortFn as sharedSprintSortFn,
	pursuitSortFn as sharedPursuitSortFn,
	enduroSortFn as sharedEnduroSortFn,
	biathlonSortFn as sharedBiathlonSortFn
} from '../utils/result-sort';

export default class ClassGroupResultsCardComponent extends Component
{
	@service store;
	@service columnPreferences;

	get classGroupId()
	{
		return this.args.classGroup?.id || this.args.classGroupId;
	}

	get allowedColumns()
	{
		let config = [];
		if(this.args.allowedColumnsString)
			config = JSON.parse(this.args.allowedColumnsString);
		else if(this.args.allowedColumns)
			config = this.args.allowedColumns;

		// Apply displayName defaults for allowed columns
		for(let column of config)
		{
			if(column.displayName == null)
				column.displayName = column.name;
		}
		return config;
	}

	get defaultColumnConfig()
	{
		// Parse the base columnConfig (before user preferences)
		let config = [];
		if(this.args.columnConfigString)
			config = JSON.parse(this.args.columnConfigString);
		else if(this.args.columnConfig)
			config = this.args.columnConfig;
		return config;
	}

	get visibleColumnNames()
	{
		// Check localStorage for user preferences via service
		const savedColumns = this.columnPreferences.getVisibleColumns(this.classGroupId);

		// If no saved preferences, use default columnConfig names
		if(!savedColumns)
		{
			return this.defaultColumnConfig.map(c => c.name);
		}
		return savedColumns;
	}

	get columnConfig()
	{
		const visibleNames = this.visibleColumnNames;

		let config = [];
		if(this.args.showColumnSelector && this.allowedColumns?.length > 0)
		{
			// Use allowedColumns order - iterate through allowedColumns and include only visible ones
			for(const allowedCol of this.allowedColumns)
			{
				if(visibleNames.includes(allowedCol.name))
				{
					config.push({ ...allowedCol });
				}
			}
		}
		else
		{
			// No column selector, use default config order
			config = [...this.defaultColumnConfig];
		}

		// Apply width and displayName defaults
		for(let column of config)
		{
			if(column.width == null)
				column.width = "";
			else
				column.width = "w-" + column.width;

			if(column.displayName == null)
				column.displayName = column.name;
		}
		return config;
	}

	@action
	toggleColumn(columnName)
	{
		const defaultColumns = this.defaultColumnConfig.map(c => c.name);
		this.columnPreferences.toggleColumn(this.classGroupId, columnName, defaultColumns);
	}

	get classGroup()
	{
		if(this.args.classGroup)
		{
			return this.args.classGroup
		}
		if(this.args.classGroupId)
		{
			return this.store.peekRecord('class-group', this.args.classGroupId);
		}

		return null;
	}

	get resultsFiltered()
	{
		let classGroupId = null;
		if(this.args.classGroup)
			classGroupId = this.args.classGroup.id;
		if(this.args.classGroupId)
			classGroupId = this.args.classGroupId;

		let classId = null;
		if(this.args.classId)
			classId = this.args.classId;

		const filterFinishedOnly = this.args.filterFinishedOnly;
		const filteredResults = this.args.results?.filter((result) =>
		{
			if(filterFinishedOnly)
			{
				if(classId)
					return (result.ClassId == classId) && (result.place != undefined) && (result.place != "");
				return (result.ClassGroupId == classGroupId) && (result.place != undefined) && (result.place != "");
			}
			else
			{
				if(classId)
					return (result.ClassId == classId);
				return (result.ClassGroupId == classGroupId);
			}
		}) || [];
		if(this.args.resultsModifier == "Pursuit")
		{
			filteredResults.sort(sharedPursuitSortFn);
			let place = 1;
			filteredResults.forEach(function(result)
			{
				if(result.totalTimeInMilliseconds)
				{
					result.place = place;
					place+=1;
				}
				else
				{
					result.place = null;
				}
			});
		}
		return filteredResults;
	}

	get sortFn()
	{
		if(this.args.sortOrder == "enduro")
			return this.enduroSortFn;
		else if(this.args.sortOrder == "biathlon")
			return this.biathlonSortFn;
		else if(this.args.sortOrder == "sprint")
			return this.sprintSortFn;
		else if(this.args.sortOrder == "pursuit")
			return this.pursuitSortFn;
		else
			return this.resultsSortFn;
	}

	@action
	enduroSortFn(a, b)
	{
		return sharedEnduroSortFn(a, b);
	}

	@action
	sprintSortFn(a, b)
	{
		return sharedSprintSortFn(a, b);
	}

	@action
	pursuitSortFn(a, b)
	{
		return sharedPursuitSortFn(a, b);
	}

	@action
	biathlonSortFn(a, b)
	{
		return sharedBiathlonSortFn(a, b);
	}

	@action
	resultsSortFn(a, b)
	{
		// Find splitName from column config if configured for single split display
		let splitName = null;
		for(const column of this.columnConfig)
		{
			if(column.name == "Splits" && column.variant == "singleSplit")
			{
				splitName = column.splitName;
			}
		}

		return sharedResultsSortFn(a, b, { splitName });
	}
}
